import { z } from "zod";
import i18n from "i18n.ts";
import {
  BookerByIdDocument,
  BookerByIdQuery,
  EnumOwnerType,
  EnumSurveyQuestionType,
  EnumSurveyQuestionBusinessScope,
  EnumSurveyQuestionWhereaboutsSearchEntityType,
  SurveyManyDocument,
  SurveyQuestionCreateManyMutation,
  useBookerUpdateMutation,
  useSurveyCreateMutation,
  useSurveyQuestionCreateManyMutation,
  useSurveyQuestionUpdateMutation,
  useSurveyUpdateMutation,
} from "gql/generated";
import { useFormHandler, usePostHog } from "shared/hooks";
import { useCallback, useMemo } from "react";
import {
  createMultilingual,
  getMultilingualString,
  updateMultilingual,
} from "shared/utils";
import { i18nNamespace, loadLanguagePacks } from "i18n";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";
import { normalizeQuestionGroups } from "./questionGroups";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Storefront]);

const optionSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, {
      message: i18n.t(
        "storefront:BOOKING_WIDGET_SETUP.QUESTIONNAIRE.CREATE_EDIT_MODAL.ERRORS.LABEL",
      ),
    }),
  isActive: z.boolean(),
  _id: z.string(),
});

export const questionSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, {
        message: i18n.t(
          "storefront:BOOKING_WIDGET_SETUP.QUESTIONNAIRE.CREATE_EDIT_MODAL.ERRORS.NAME",
        ),
      }),
    type: z.enum([
      "SHORT_TEXT",
      "LONG_TEXT",
      "RICH_TEXT",
      "BOOLEAN",
      "SELECT",
      "DATE",
      "WHEREABOUTS_SEARCH",
      "ADDRESS_SEARCH",
      "SECTION_HEADING",
      "CUSTOM_TEXT",
      "TOGGLE",
    ]),
    required: z.boolean(),
    isActive: z.boolean(),
    options: z.array(optionSchema),
    whereaboutsSearchEntityType: z.enum(["OPERATOR"]).optional(),
    description: z.string().optional(),
    /** A toggle's id (possibly a not-yet-saved temp id). */
    parentQuestion: z.string().optional(),
    /** Toggles only: answered once per selected business. */
    businessScope: z.enum(["SHARED", "PER_BUSINESS"]).optional(),
    _id: z.string(),
  })
  .superRefine((values, ctx) => {
    if (
      values.type === EnumSurveyQuestionType.Select &&
      values.options.length < 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: i18n.t(
          "storefront:BOOKING_WIDGET_SETUP.QUESTIONNAIRE.CREATE_EDIT_MODAL.ERRORS.OPTIONS",
        ),
      });
      return;
    }
  });

const surveySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, {
      message: i18n.t(
        "storefront:BOOKING_WIDGET_SETUP.QUESTIONNAIRE.CREATE_EDIT_MODAL.ERRORS.NAME",
      ),
    }),
  questions: z.array(questionSchema).min(1, {
    message: i18n.t(
      "storefront:BOOKING_WIDGET_SETUP.QUESTIONNAIRE.CREATE_EDIT_MODAL.ERRORS.QUESTIONS",
    ),
  }),
});

export type QuestionFormValues = z.infer<typeof questionSchema>;
export type QuestionnaireFormValues = z.infer<typeof surveySchema>;

const questionTypeSupportsOptions = (type?: EnumSurveyQuestionType | string) =>
  type === EnumSurveyQuestionType.Select ||
  type === EnumSurveyQuestionType.Boolean;

export type QuestionnaireFormOwner =
  | { type: "storefront"; storefrontId: string }
  | { type: "organization"; entityId: string };

type UseCreateEditQuestionnaireModalFormOptions = {
  /** When owner is not provided, storefrontId and bookerId are required (Storefront flow). */
  storefrontId?: string;
  bookerId?: string;
  /** When provided, used for survey/question owner instead of storefront (e.g. Broadcasts/Organization flow). */
  owner?: QuestionnaireFormOwner;
  survey: NonNullable<BookerByIdQuery["bookerById"]>["survey"] | null;
  onSuccess?: (surveyId: string) => void;
  onError?: () => void;
};

export const useCreateEditQuestionnaireModalForm = ({
  storefrontId = "",
  bookerId = "",
  owner,
  survey,
  onError,
  onSuccess,
}: UseCreateEditQuestionnaireModalFormOptions) => {
  const ownerForSurvey =
    owner?.type === "organization"
      ? { type: EnumOwnerType.Organization, entityId: owner.entityId }
      : { type: EnumOwnerType.Storefront, entityId: storefrontId };
  const defaultValues: QuestionnaireFormValues = useMemo(
    () => ({
      name: getMultilingualString(survey?.name ?? {}),
      questions: survey?.questions
        ? normalizeQuestionGroups(
            survey.questions
              .filter(q => q.isActive) // Only show the active questions
              .map(q => ({
                _id: q._id,
                isActive: q.isActive,
                name: getMultilingualString(q.name),
                type: q.type,
                required: q.required,
                whereaboutsSearchEntityType:
                  q.whereaboutsSearchEntityType ?? undefined,
                description: q.description
                  ? getMultilingualString(q.description)
                  : undefined,
                parentQuestion: q.parentQuestion ?? undefined,
                businessScope:
                  (q.businessScope as "SHARED" | "PER_BUSINESS" | null) ?? undefined,
                options: q.options
                  .filter(op => op?.isActive) // Only show the active options
                  .map(op => ({
                    _id: op?._id ?? "",
                    isActive: op?.isActive ?? false,
                    label: getMultilingualString(op?.label ?? {}),
                  })),
              })),
          )
        : [],
    }),
    [survey],
  );

  const { trackPostHogException } = usePostHog();

  const [createSurvey] = useSurveyCreateMutation({
    refetchQueries: [SurveyManyDocument],
    awaitRefetchQueries: true,
  });
  const [updateSurvey] = useSurveyUpdateMutation({
    refetchQueries: [SurveyManyDocument],
    awaitRefetchQueries: true,
  });
  const [createSurveyQuestions] = useSurveyQuestionCreateManyMutation();
  const [updateSurveyQuestion] = useSurveyQuestionUpdateMutation();
  const [updateBooker] = useBookerUpdateMutation({
    refetchQueries: [BookerByIdDocument, SurveyManyDocument],
    awaitRefetchQueries: true,
  });

  const handleUpdateBooker = useCallback(
    async (surveyId: string) => {
      await updateBooker({
        variables: {
          id: bookerId,
          record: {
            survey: surveyId,
          },
        },
      });
    },
    [bookerId, updateBooker],
  );

  return useFormHandler<QuestionnaireFormValues>(
    useCallback(
      async data => {
        if (data) {
          try {
            const questions = normalizeQuestionGroups(
              (data.questions ?? []).filter(
                (q): q is QuestionnaireFormValues["questions"][number] =>
                  !!q?._id && !!q.type,
              ),
            );
            const newQuestions = questions.filter(
              q => !q?._id || q._id.length < 24,
            ); // get all questions that don't have a objectId, they are new questions

            const newQuestionDocs: NonNullable<
              SurveyQuestionCreateManyMutation["surveyQuestionCreateMany"]
            >["records"] = [];

            if (newQuestions && newQuestions.length > 0) {
              // If there's new questions, create them in the DB
              const { data: newQuestionsData } = await createSurveyQuestions({
                variables: {
                  records: newQuestions.map(q => ({
                    isActive: true,
                    name: createMultilingual(q?.name ?? ""),
                    type: q?.type
                      ? (q.type as EnumSurveyQuestionType)
                      : EnumSurveyQuestionType.ShortText,
                    required:
                      isSurveyDesignFieldType(q?.type) ||
                      q?.type === EnumSurveyQuestionType.Toggle
                        ? false
                        : (q?.required ?? false),
                    options: questionTypeSupportsOptions(q?.type)
                      ? q?.options?.map(op => ({
                          isActive: true,
                          label: createMultilingual(op?.label ?? ""),
                        }))
                      : undefined,
                    whereaboutsSearchEntityType:
                      q?.type === EnumSurveyQuestionType.WhereaboutsSearch
                        ? (q?.whereaboutsSearchEntityType as EnumSurveyQuestionWhereaboutsSearchEntityType)
                        : undefined,
                    description:
                      q?.description && !isSurveyDesignFieldType(q.type)
                        ? createMultilingual(q.description)
                        : undefined,
                    businessScope:
                      q?.type === EnumSurveyQuestionType.Toggle && q.businessScope
                        ? (q.businessScope as EnumSurveyQuestionBusinessScope)
                        : undefined,
                    owner: ownerForSurvey,
                  })),
                },
              });

              // Push those new questions to the array of question docs
              if (newQuestionsData?.surveyQuestionCreateMany?.records) {
                newQuestionDocs.push(
                  ...newQuestionsData.surveyQuestionCreateMany.records,
                );
              }
            }

            // Build a map from each new question's temp _id to its real MongoDB _id.
            // createSurveyQuestions returns records in the same order, so index-based matching is reliable.
            const tempIdToRealId = new Map<string, string>();
            newQuestions?.forEach((q, i) => {
              if (q?._id && newQuestionDocs[i]?._id) {
                tempIdToRealId.set(q._id, newQuestionDocs[i]._id);
              }
            });

            const resolveQuestionId = (id?: string) =>
              !id ? undefined : id.length === 24 ? id : tempIdToRealId.get(id);
            const resolveQuestionIds = (list: typeof questions) =>
              list
                .map(q => resolveQuestionId(q?._id))
                .filter((id): id is string => id != null);

            // Follow-up links are written after creation so a follow-up can
            // point at a toggle created in this same save.
            const savedParentById = new Map(
              (survey?.questions ?? []).map(q => [
                q._id,
                q.parentQuestion ?? undefined,
              ]),
            );
            for (const q of questions) {
              const id = resolveQuestionId(q._id);
              if (!id) continue;
              const parentQuestion = resolveQuestionId(q.parentQuestion);
              if (parentQuestion === savedParentById.get(id)) continue;
              await updateSurveyQuestion({
                variables: {
                  id,
                  record: { parentQuestion: parentQuestion ?? null },
                },
              });
            }

            let newSurveyId: string | undefined = survey?._id;

            // Only update when the survey is persisted (has a MongoID). In-memory seeds (e.g. inquiry
            // widget placeholder) are truthy but have no _id and must use surveyCreate.
            if (survey?._id) {
              // If there's an existing survey, update it with the questions and/or updated name
              const { data: updatedSurvey } = await updateSurvey({
                variables: {
                  id: survey._id,
                  record: {
                    name: updateMultilingual(survey.name, data.name ?? ""),
                    questions: resolveQuestionIds(questions),
                  },
                },
              });

              if (updatedSurvey?.surveyUpdate?.record?._id) {
                if (bookerId) {
                  await handleUpdateBooker(
                    updatedSurvey.surveyUpdate.record._id,
                  );
                }
                newSurveyId = updatedSurvey.surveyUpdate.record._id;
              } else if (bookerId) {
                throw new Error("Failed to add survey to booker");
              }
            } else {
              // If there's no existing survey, create a new one
              const { data: newSurvey } = await createSurvey({
                variables: {
                  record: {
                    name: createMultilingual(data?.name ?? ""),
                    questions: resolveQuestionIds(questions),
                    owner: ownerForSurvey,
                  },
                },
              });

              if (newSurvey?.surveyCreate?.record?._id) {
                if (bookerId) {
                  await handleUpdateBooker(newSurvey.surveyCreate.record._id);
                }
                newSurveyId = newSurvey.surveyCreate.record._id;
              } else if (bookerId) {
                throw new Error("Failed to add survey to booker");
              }
            }
            onSuccess?.(newSurveyId ?? "");
          } catch (error) {
            trackPostHogException(error, {
              fileName: "useCreateEditQuestionnaireModalForm.ts",
              method: "handleSubmit",
              message: "Error submitting questionnaire form",
            });
            onError?.();
          }
        }
      },
      [
        survey,
        onSuccess,
        createSurveyQuestions,
        updateSurveyQuestion,
        ownerForSurvey,
        updateSurvey,
        bookerId,
        handleUpdateBooker,
        createSurvey,
        onError,
        trackPostHogException,
      ],
    ),
    defaultValues,
    { schema: surveySchema, mode: "onBlur" },
  );
};
